import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { JourneyPath } from "@/components/JourneyPath";
import { useBlocks, journeyStations } from "@/hooks/useContent";
import { DependentPicker } from "@/components/forms/FieldInput";
import { PartnerInvite } from "@/pages/portal/PartnerInvite";
import { FreelyGiven } from "@/components/FreelyGiven";
import { t } from "@/lib/i18n";
import { STATUS, fmtDateTime } from "@/lib/scheduling";

const fmt = (d) => new Date(d).toLocaleDateString("en-US", { timeZone: "America/Los_Angeles", dateStyle: "medium" });
const HOUSEHOLD_FIELD = { key: "household_dep", label: "Children in your household", required: false };

export async function openDocument(id) {
  try { const { data } = await api.post(`/documents/${id}/link`); window.open(data.url, "_blank", "noopener"); } catch (e) { toast.error(apiError(e)); }
}

const Section = ({ id, title, children, testId }) => (
  <section id={id} className="border-t border-sage-400/60 pt-5" data-testid={testId} aria-labelledby={`${testId}-h`}>
    <h2 id={`${testId}-h`} className="font-serif text-xl font-semibold">{title}</h2>
    <div className="mt-2 text-ink-500 max-w-[55ch]">{children}</div>
  </section>
);

export default function PortalHome() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: blocks } = useBlocks();
  const { data: journey } = useQuery({ queryKey: ["portal", "journey"], queryFn: async () => (await api.get("/portal/journey")).data });
  const { data: kase } = useQuery({ queryKey: ["portal", "case"], queryFn: async () => (await api.get("/portal/case")).data });
  const { data: series } = useQuery({ queryKey: ["portal", "series"], queryFn: async () => (await api.get("/portal/series")).data, enabled: !!kase });
  const { data: sched } = useQuery({ queryKey: ["scheduling", "my"], queryFn: async () => (await api.get("/scheduling/my")).data, enabled: !!kase });
  const { data: msgs } = useQuery({ queryKey: ["messages", "unread"], queryFn: async () => (await api.get("/messages/unread")).data, enabled: !!kase });
  const { data: recs } = useQuery({ queryKey: ["portal", "resources"], queryFn: async () => (await api.get("/portal/resources")).data, enabled: !!kase });
  const nextAppt = (sched?.appointments || []).filter((a) => ["requested", "confirmed"].includes(a.status) && !a.is_past).sort((x, y) => new Date(x.start_at) - new Date(y.start_at))[0];
  const [resent, setResent] = useState("");
  const [showHousehold, setShowHousehold] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem("partner_invite_token");
    if (token) navigate(`/join?token=${token}`, { replace: true });
  }, [navigate]);

  const resend = async () => { try { await api.post("/auth/resend-verification"); setResent(t("portal.resent")); } catch (e) { setResent(apiError(e)); } };
  const requestRestricted = async () => { try { await api.post("/portal/case/request-restricted"); qc.invalidateQueries({ queryKey: ["portal"] }); toast.success("Your case is now Restricted."); } catch (e) { toast.error(apiError(e)); } };
  const revokeRoi = async (id) => { if (!window.confirm("Revoke this authorization? PCM will stop sharing information under it.")) return; try { await api.post(`/portal/roi/${id}/revoke`); qc.invalidateQueries({ queryKey: ["portal"] }); toast.success("Authorization revoked."); } catch (e) { toast.error(apiError(e)); } };
  const stations = journeyStations(blocks).map((s) => ({ ...s, body: undefined }));
  const notYet = kase?.state === "not_yet_eligible";
  const forms = (kase?.documents || []).filter((d) => d.kind !== "certificate");

  return (
    <div data-testid="portal-home">
      <section className="bg-olive-900 text-cream-50">
        <div className="mx-auto max-w-6xl px-4 pb-14 pt-10 sm:px-6">
          <p className="text-coral-300 font-semibold">{t("portal.greeting", { name: user.first_name })}</p>
          <h1 className="display-italic mt-1 text-4xl sm:text-5xl text-cream-50">{t("portal.title")}</h1>
          {!user.email_verified && (
            <div className="mt-6 max-w-2xl border border-cream-50/40 bg-cream-50 p-4 text-ink-900" role="status" data-testid="verify-email-banner">
              <p>{t("portal.verifyBanner", { email: user.email })}</p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button type="button" onClick={resend} className="btn-secondary min-h-[40px]" data-testid="verify-email-resend">{t("portal.resend")}</button>
                {resent && <span className="text-sm text-ink-500" role="status">{resent}</span>}
              </div>
            </div>
          )}
          <div className="mt-10 max-w-2xl">
            {journey && <JourneyPath stations={stations} currentIndex={journey.station_index} nextAction={journey.next_action} onOlive />}
          </div>
          {kase && <p className="mt-8 text-sm text-cream-50/70" data-testid="portal-case-code">Case code {kase.case_code}{kase.restricted ? " · Restricted" : ""}{kase.eligibility_status === "pending" ? " · eligibility check in progress" : kase.eligibility_status === "verified" ? " · eligibility confirmed" : ""}{kase.counselor ? ` · counselor ${kase.counselor.display_name}` : ""}</p>}
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
        {notYet && (
          <section id="next-steps" className="surface mb-12 max-w-3xl p-6" data-testid="portal-next-steps">
            <h2 className="text-2xl">Your Next Step</h2>
            <p className="mt-2">Pastoral counseling opens to members who complete Growth Track or attend regularly for six months or more. Growth Track is the fastest path, and everything below is open to you today.</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <a href="https://www.patria.church/growth-track" target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="next-steps-growth-track">Growth Track</a>
              <Link to="/resources" className="btn-secondary">Resources</Link>
              <Link to="/life-care-classes" className="btn-secondary">Life Care Classes</Link>
              <Link to="/pray-with-us" className="btn-secondary">Pray With Us</Link>
              <Link to="/reach-out" className="btn-secondary">Reach Out</Link>
            </div>
          </section>
        )}
        <div className="grid gap-x-12 gap-y-10 md:grid-cols-2" data-testid="portal-sections">
          <Section title={t("portal.sections.appointments")} testId="portal-section-appointments">
            {sched?.case && sched.counselor ? (
              <div className="text-ink-900">
                {nextAppt ? (
                  <><p className="font-semibold" data-testid="portal-next-appointment">{fmtDateTime(nextAppt.start_at)} Pacific</p><p className="text-sm text-ink-500">{STATUS[nextAppt.status]?.label} · {nextAppt.location} · with {sched.counselor.display_name}</p></>
                ) : <p>{sched.can_book ? `You are matched with ${sched.counselor.display_name}. Choose a time, and Patria will confirm it by email.` : sched.reason}</p>}
                {sched.sessions && <p className="mt-2 text-sm text-ink-500">{sched.sessions.used} of {sched.sessions.limit} free sessions used</p>}
                <Link to="/portal/appointments" className="btn-primary mt-4" data-testid="portal-appointments-link">{nextAppt ? "My Appointments" : sched.can_book ? "Request An Appointment" : "Appointments"}</Link>
              </div>
            ) : t("portal.sections.appointmentsEmpty")}
          </Section>
          <Section title={t("portal.messages")} testId="portal-section-messages">
            <p>{msgs?.unread ? `You have ${msgs.unread} new message${msgs.unread === 1 ? "" : "s"} from the care team.` : "Write to the care team anytime — a question about sessions, something your counselor should know ahead of time, or how you are doing."}</p>
            <Link to="/portal/messages" className="btn-secondary mt-4" data-testid="portal-messages-link">{msgs?.unread ? "Read Messages" : "Message The Care Team"}</Link>
          </Section>
          <Section id="videos" title={t("portal.sections.videos")} testId="portal-section-videos">
            {series?.available ? (
              <div className="text-ink-900">
                <p>{series.series.title} · {series.completed_count} of {series.total} sessions complete</p>
                <div className="mt-2 h-1.5 w-full max-w-xs bg-sage-400/40"><div className="h-full bg-olive-900" style={{ width: `${(series.completed_count / Math.max(series.total, 1)) * 100}%` }} /></div>
                <Link to="/portal/videos" className="btn-primary mt-4" data-testid="portal-videos-link">{series.all_complete ? "Review Your Series" : "Continue Watching"}</Link>
              </div>
            ) : t("portal.sections.videosEmpty")}
          </Section>
          <Section title={t("portal.sections.certificates")} testId="portal-section-certificates">
            {kase?.certificates?.length ? (
              <ul className="space-y-3 text-ink-900">
                {kase.certificates.map((c) => (
                  <li key={c.id} data-testid={`certificate-${c.id}`}>
                    <p className="font-semibold">{c.series_title}</p>
                    <p className="text-sm text-ink-500">No. <span className="font-mono">{c.number}</span> · issued {fmt(c.issued_at)}</p>
                    <div className="mt-1.5 flex flex-wrap gap-3"><button type="button" className="link" onClick={() => openDocument(c.document_id)} data-testid={`download-certificate-${c.id}`}>Download PDF</button><Link to={`/verify/${c.number}`} className="link" data-testid={`verify-certificate-${c.id}`}>Verification page</Link></div>
                  </li>
                ))}
              </ul>
            ) : t("portal.sections.certificatesEmpty")}
          </Section>
          <Section title={t("portal.sections.forms")} testId="portal-section-forms">
            {forms.length ? (
              <ul className="space-y-2 text-ink-900">
                {forms.map((d) => (
                  <li key={d.id} className="flex flex-wrap items-center justify-between gap-2">
                    <span>{d.title} <span className="text-ink-500">· {fmt(d.created_at)}</span></span>
                    <button type="button" className="link" onClick={() => openDocument(d.id)} data-testid={`download-doc-${d.id}`}>Download</button>
                  </li>
                ))}
              </ul>
            ) : t("portal.sections.formsEmpty")}
            {kase?.state === "certificate_issued" && <Link to="/portal/in-person-intake" className="btn-primary mt-4" data-testid="portal-in-person-intake-link">Complete In-Person Intake</Link>}
          </Section>
          <Section title={t("portal.sections.classes")} testId="portal-section-classes">{t("portal.sections.classesEmpty")}</Section>
          <Section id="recommended" title={t("portal.sections.recommended")} testId="portal-section-recommended">
            {recs?.assignments?.length ? (
              <ul className="space-y-4 text-ink-900" data-testid="recommended-list">
                {recs.assignments.map((a) => (
                  <li key={a.id} data-testid={`recommended-${a.id}`}>
                    <p className="font-semibold">{a.resource.title}{!a.viewed && <span className="ml-2 border border-terracotta-600 px-1.5 py-0.5 text-xs font-semibold text-terracotta-600">New</span>}</p>
                    {a.note && <p className="mt-0.5 text-sm italic">“{a.note}”</p>}
                    <p className="mt-0.5 text-sm text-ink-500">{a.resource.description}</p>
                    <p className="mt-1 text-xs text-ink-500">From {user.discreet_mode ? "your care team" : a.assigned_by} · {fmt(a.assigned_at)}</p>
                    {a.resource.url && <a href={a.resource.url} target="_blank" rel="noopener noreferrer" className="link mt-1 inline-block text-sm" data-testid={`recommended-open-${a.id}`}>Open {a.resource.title}</a>}
                  </li>
                ))}
              </ul>
            ) : t("portal.sections.recommendedEmpty")}
          </Section>
          <Section title={t("portal.sections.household")} testId="portal-section-household">
            {kase && ["relationship", "family"].includes(kase.care_for) && <PartnerInvite kase={kase} />}
            {showHousehold ? (
              <div className="mt-4 text-ink-900"><DependentPicker field={HOUSEHOLD_FIELD} value={null} onChange={() => {}} /></div>
            ) : (
              <><p className="mt-3">Add a child so you can complete forms and booking for them.</p><button type="button" className="btn-secondary mt-3" onClick={() => setShowHousehold(true)} data-testid="household-manage">Manage Household</button></>
            )}
          </Section>
          <Section title="Privacy" testId="portal-section-privacy">
            {kase ? (kase.restricted ? "Your case is Restricted. Only your assigned counselor, that counselor's supervisor, and the Care Director can see it." : (
              <><p>You may ask for Restricted status at any time: only your assigned counselor, their supervisor, and the Care Director will be able to see your case.</p><button type="button" className="btn-secondary mt-3" onClick={requestRestricted} data-testid="request-restricted">Ask For Restricted Status</button></>
            )) : "Restricted status becomes available once your Care Intake is submitted."}
            {kase?.releases?.length > 0 && (
              <div className="mt-5 text-ink-900" data-testid="portal-releases">
                <p className="font-semibold">Release of Information authorizations</p>
                <ul className="mt-2 space-y-2">
                  {kase.releases.map((r) => (
                    <li key={r.id} className="flex flex-wrap items-center justify-between gap-2" data-testid={`roi-${r.id}`}>
                      <span>{r.recipient} <span className="text-ink-500">· {r.revoked_at ? "revoked" : `expires ${r.expires_on}`}</span></span>
                      <span className="flex gap-3">{r.document_id && <button type="button" className="link" onClick={() => openDocument(r.document_id)}>PDF</button>}{!r.revoked_at && <button type="button" className="link text-terracotta-600" onClick={() => revokeRoi(r.id)} data-testid={`roi-revoke-${r.id}`}>Revoke</button>}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Section>
          <Section title={t("portal.sections.records")} testId="portal-section-records">{t("portal.sections.recordsEmpty")}</Section>
        </div>
        <FreelyGiven variant="quiet" testId="portal-giving" />
        <div className="mt-14 flex flex-col gap-3 sm:flex-row">
          <Link to="/portal/preferences" className="btn-secondary" data-testid="portal-preferences-link">{t("portal.preferences")}</Link>
          <Link to="/resources" className="btn-secondary" data-testid="portal-resources-link">{t("nav.resources")}</Link>
        </div>
      </div>
    </div>
  );
}
